// All date filtering/display on this dashboard is anchored to America/New_York,
// regardless of the visitor's browser timezone or the server's clock. This
// mirrors callsDashboard's pattern of computing explicit from/to boundaries
// client-side and always sending them to the API (never a bare "period"
// keyword), so the backend never has to guess a timezone.

export const NY_TZ = "America/New_York";

// Returns the millisecond offset such that:
//   wallClockInTz = date.getTime() + offset
// Computed via Intl so it's correct across EST/EDT without a DST table.
function tzOffsetMs(date, timeZone) {
  const dtf = new Intl.DateTimeFormat("en-US", {
    timeZone,
    hourCycle: "h23",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  const asUTC = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUTC - date.getTime();
}

// Converts a NY wall-clock date/time (y, m, d, h, mi, s) into the real UTC
// Date instant it corresponds to.
function nyWallClockToUtc(y, m, d, h = 0, mi = 0, s = 0) {
  const naiveUtcMs = Date.UTC(y, m - 1, d, h, mi, s);
  const offset = tzOffsetMs(new Date(naiveUtcMs), NY_TZ);
  return new Date(naiveUtcMs - offset);
}

// { year, month, day } for "now", as seen on a New York wall clock.
export function nyTodayParts(date = new Date()) {
  const dtf = new Intl.DateTimeFormat("en-US", { timeZone: NY_TZ, year: "numeric", month: "2-digit", day: "2-digit" });
  const parts = dtf.formatToParts(date).reduce((acc, p) => {
    if (p.type !== "literal") acc[p.type] = p.value;
    return acc;
  }, {});
  return { year: Number(parts.year), month: Number(parts.month), day: Number(parts.day) };
}

// Formats a UTC ISO string / Date as "YYYY-MM-DD" for a date input, using NY.
export function toNyDateInputValue(dateOrIso) {
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  const { year, month, day } = nyTodayParts(d);
  return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

// Given a "YYYY-MM-DD" NY calendar date, returns the UTC instant range
// [00:00:00.000, 23:59:59.999] for that NY day.
export function nyDayRange(dateStr) {
  const [y, m, d] = dateStr.split("-").map(Number);
  const from = nyWallClockToUtc(y, m, d, 0, 0, 0);
  const to = nyWallClockToUtc(y, m, d, 23, 59, 59);
  return { from: from.toISOString(), to: new Date(to.getTime() + 999).toISOString() };
}

// Given "YYYY-MM-DD" NY calendar dates for the start and end of a picked
// custom range, returns the UTC instant range covering the full NY days.
export function nyCustomRange(fromStr, toStr) {
  const from = fromStr ? nyDayRange(fromStr).from : undefined;
  const to = toStr ? nyDayRange(toStr).to : undefined;
  return { from, to };
}

// Quick-period buttons, anchored to "today" in New York.
export function nyPeriodRange(period) {
  const { year, month, day } = nyTodayParts();
  const to = nyWallClockToUtc(year, month, day, 23, 59, 59);
  const toIso = new Date(to.getTime() + 999).toISOString();

  const start = new Date(Date.UTC(year, month - 1, day));
  switch (period) {
    case "weekly":
      start.setUTCDate(start.getUTCDate() - 7);
      break;
    case "monthly":
      start.setUTCMonth(start.getUTCMonth() - 1);
      break;
    case "quarterly":
      start.setUTCMonth(start.getUTCMonth() - 3);
      break;
    case "annually":
      start.setUTCFullYear(start.getUTCFullYear() - 1);
      break;
    case "daily":
    default:
      break;
  }

  const from = nyWallClockToUtc(start.getUTCFullYear(), start.getUTCMonth() + 1, start.getUTCDate(), 0, 0, 0);
  return { from: from.toISOString(), to: toIso };
}

// For displaying any timestamp that comes back from the API, always in NY time.
export function formatNy(dateOrIso, opts = {}) {
  if (!dateOrIso) return "";
  const d = dateOrIso instanceof Date ? dateOrIso : new Date(dateOrIso);
  return new Intl.DateTimeFormat("en-US", { timeZone: NY_TZ, dateStyle: "medium", timeStyle: "short", ...opts }).format(d);
}
